import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
  ReferenceLine,
  Customized
} from 'recharts';
import moment from 'moment';
import { Pencil, Pin, X, ChevronRight, Hash, Search, MousePointer2, Minus, Eraser, Trash2, Download } from 'lucide-react';

const PALETTE = [
  "#e6194b", "#3cb44b", "#4363d8", "#f58231", "#911eb4",
  "#42d4f4", "#f032e6", "#bfef45", "#fabed4", "#469990",
  "#dcbeff", "#9A6324", "#fffac8", "#800000", "#aaffc3",
  "#808000", "#ffd8b1", "#000075", "#a9a9a9", "#e6beff",
  "#ffe119", "#00ff7f", "#ff6347", "#7b68ee", "#00ced1",
  "#ff1493", "#ffa07a", "#98fb98", "#dda0dd", "#87ceeb",
  "#cd853f", "#20b2aa", "#ff4500", "#1e90ff", "#ffffff",
];

const CustomTooltip = ({ active, label }) => {
  if (active && label) {
    return (
      <div className="bg-zinc-900/90 border border-zinc-700 px-3 py-1 rounded shadow-xl backdrop-blur-sm pointer-events-none">
        <div className="text-white font-mono text-[13px] font-black tracking-wider uppercase">
          {moment(label).format('DD MMM YYYY')}
        </div>
      </div>
    );
  }
  return null;
};

// Data inspection panel component for the right side
const DataPanel = ({ data, activeSeries, lockedSeries, date, onClearLock, isLocked, setActiveSeries, toggleLock, colorMap, showAllLines, setShowAllLines, isResizable, viewMode, setViewMode }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem('rs_panel_width_v3');
      if (saved) return parseInt(saved, 10);
    } catch(e) {}
    return 320;
  });

  const panelRef = useRef(null);
  const widthRef = useRef(panelWidth);

  useEffect(() => {
    let timeoutId;
    const observer = new ResizeObserver((entries) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        for (let entry of entries) {
          if (entry.target === panelRef.current) {
            const w = entry.borderBoxSize ? entry.borderBoxSize[0].inlineSize : entry.contentRect.width;
              if (Math.abs(w - widthRef.current) > 5) {
                widthRef.current = Math.round(w);
                setPanelWidth(Math.round(w));
                localStorage.setItem('rs_panel_width_v3', Math.round(w).toString());
              }
          }
        }
      }, 300);
    });

    if (panelRef.current) observer.observe(panelRef.current);
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  // Extract values for the current date
  const dateStr = date ? String(date) : null;
  const dateIndex = data.findIndex(e => String(e.date) === dateStr);
  const dateEntry = dateIndex !== -1 ? data[dateIndex] : null;
  if (!dateEntry) return null;

  let fullRankings = [];

  if (viewMode === 'RS_VALUES') {
    fullRankings = Object.entries(dateEntry)
      .filter(([key, val]) => key !== 'date' && val !== null && val !== undefined)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => (b.value || 0) - (a.value || 0));
  } else {
    // TOP_GAINERS logic: rank jump from previous day to current day
    const pastIndex = Math.max(0, dateIndex - 1);
    const pastEntry = data[pastIndex];

    const getRanks = (entry) => {
      if (!entry) return {};
      const sorted = Object.entries(entry)
        .filter(([key, val]) => key !== 'date' && val !== null)
        .sort((a, b) => b[1] - a[1]);
      
      const ranks = {};
      sorted.forEach(([name, _], index) => {
        ranks[name] = index + 1;
      });
      return ranks;
    };

    const pastRanks = getRanks(pastEntry);
    const currentRanks = getRanks(dateEntry);

    fullRankings = Object.entries(currentRanks)
      .map(([name, todayRank]) => {
        const yesterdayRank = pastRanks[name];
        let jump = null;
        if (yesterdayRank !== undefined) {
          jump = yesterdayRank - todayRank; // positive means moved up
        }
        return { name, value: jump, isRankJump: true };
      })
      .filter(item => item.value !== null)
      .sort((a, b) => b.value - a.value);
  }

  const rankings = showAllLines ? fullRankings : fullRankings.slice(0, 10);

  const handleDownload = () => {
    // Filter visible list by search query if present
    const filtered = rankings.filter(entry => 
      viewMode === 'TOP_GAINERS' || entry.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formattedDate = moment(date).format('YYYY-MM-DD');
    const valueHeader = viewMode === 'TOP_GAINERS' ? 'Rank Jump' : 'RSC Value';
    
    let csvContent = "Date,Rank,Symbol," + valueHeader + "\n";
    filtered.forEach((entry, idx) => {
      const valStr = entry.value !== undefined && entry.value !== null 
        ? (entry.isRankJump 
            ? (entry.value > 0 ? `+${entry.value}` : entry.value) 
            : entry.value.toFixed(6)) 
        : '—';
      const symbolClean = entry.name.replace(/"/g, '""');
      csvContent += `${formattedDate},${idx + 1},"${symbolClean}",${valStr}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    const filename = `live_inspection_${formattedDate}_${viewMode.toLowerCase()}.csv`;
    link.setAttribute("download", filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div 
      ref={panelRef}
      style={{ width: panelWidth, minWidth: '280px', maxWidth: '600px' }}
      className={`flex flex-col border-r border-zinc-800 bg-zinc-900/40 relative shrink-0 ${isResizable ? 'resize-x overflow-hidden' : 'overflow-hidden'}`}
    >
      <div className="p-4 border-b border-zinc-800 bg-zinc-900/60 sticky top-0 z-10 backdrop-blur-md">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-2">
            <div className="mt-0.5">
              {isLocked ? (
                <Pin className="w-4 h-4 text-emerald-500 fill-emerald-500/20" />
              ) : (
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              )}
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className={`text-[15px] font-black uppercase tracking-tighter ${isLocked ? 'text-emerald-400' : 'text-white'}`}>
                  {isLocked ? 'POINT LOCKED' : 'LIVE INSPECTION'}
                </span>
                <span className="text-zinc-600 text-[11px]">•</span>
                <span className="text-zinc-300 font-mono text-[12px] font-black tracking-tight">
                  {moment(date).format('DD MMM YYYY')}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 -mt-1">
            <button
              onClick={handleDownload}
              className="p-1 hover:bg-zinc-800 text-zinc-500 hover:text-white rounded transition-colors"
              title="Download CSV"
            >
              <Download className="w-4 h-4" />
            </button>
            {isLocked && (
              <button
                onClick={onClearLock}
                className="p-1 hover:bg-zinc-800 text-zinc-500 hover:text-white rounded transition-colors"
                title="Clear Point Lock"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="mt-3 flex flex-col gap-2">
          {/* Mode toggle */}
          <div className="flex bg-zinc-900/80 rounded p-1 border border-zinc-700/50">
            <button
              onClick={() => { setViewMode('RS_VALUES'); setSearchTerm(''); }}
              className={`flex-1 text-[12px] py-1 px-2 rounded transition-colors font-bold ${viewMode === 'RS_VALUES' ? 'bg-blue-600 text-white shadow-sm' : 'text-white hover:text-zinc-200'}`}
            >
              RS RANK
            </button>
            <button
              onClick={() => { setViewMode('TOP_GAINERS'); setSearchTerm(''); }}
              className={`flex-1 text-[12px] py-1 px-2 rounded transition-colors font-bold ${viewMode === 'TOP_GAINERS' ? 'bg-emerald-600 text-white shadow-sm' : 'text-white hover:text-zinc-200'}`}
            >
              TOP GAINERS
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {viewMode === 'RS_VALUES' && (
          <div className="mt-3 relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input
              type="text"
              placeholder="Search stocks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-zinc-800/50 border border-zinc-700/50 rounded-md py-1.5 pl-8 pr-8 text-[13px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500 transition-colors"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:text-white text-zinc-500"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto custom-scroll p-3 space-y-1.5">
        {rankings
          .filter(entry => viewMode === 'TOP_GAINERS' || entry.name.toLowerCase().includes(searchTerm.toLowerCase()))
          .map((entry, index) => {
            const isActive = entry.name === activeSeries;
            const isLkd = lockedSeries?.has(entry.name);
            const isHighlighted = isActive || isLkd;

            return (
              <div
                key={entry.name}
                onMouseEnter={() => setActiveSeries(entry.name)}
                onMouseLeave={() => setActiveSeries(null)}
                onClick={() => toggleLock(entry.name)}
                className={`flex items-center justify-between p-2 rounded-lg transition-all border cursor-pointer ${isActive
                  ? 'bg-white/10 border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.05)]'
                  : isLkd
                    ? 'bg-emerald-500/10 border-emerald-500/20'
                    : 'bg-zinc-900/50 border-transparent hover:bg-zinc-800/50'
                  }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex flex-col items-center min-w-[24px]">
                    <span className="text-[12px] text-white font-black mb-0.5">#{index + 1}</span>
                    <div className="w-3 h-3 rounded-full mt-0.5 shadow-sm border border-black/20" style={{ backgroundColor: colorMap[entry.name] }} />
                  </div>
                  <span className={`text-[13px] font-black uppercase truncate ${isHighlighted ? 'text-white' : 'text-white'}`}>
                    {entry.name}
                  </span>
                </div>
                <div className="flex flex-col items-end">
                  <span className={`font-mono text-[13px] font-black ${
                    isHighlighted 
                      ? 'text-white' 
                      : (entry.isRankJump 
                          ? (entry.value > 0 ? 'text-emerald-400' : (entry.value < 0 ? 'text-rose-400' : 'text-white'))
                          : 'text-white')
                  }`}>
                    {entry.value !== undefined && entry.value !== null ? 
                      (entry.isRankJump 
                        ? (entry.value > 0 ? `↑ ${entry.value}` : (entry.value < 0 ? `↓ ${Math.abs(entry.value)}` : '-'))
                        : entry.value.toFixed(4))
                      : '—'}
                  </span>
                  {isLkd && <span className="text-[12px] text-emerald-500 font-bold tracking-widest">PINNED</span>}
                </div>
              </div>
            );
          })}
      </div>

      {!searchTerm && viewMode === 'RS_VALUES' && (
        <div className="p-3 border-t border-zinc-800 bg-zinc-900/60">
          <button
            onClick={() => setShowAllLines(!showAllLines)}
            className="w-full py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-[12px] font-bold rounded transition-colors"
          >
            {showAllLines ? 'SHOW LESS' : 'SEE ALL'}
          </button>
        </div>
      )}
    </div>
  );
};

const ChartStateCatcher = ({ yAxisMap, onScaleUpdate }) => {
  useEffect(() => {
    if (yAxisMap && yAxisMap[0] && yAxisMap[0].scale) {
      onScaleUpdate(yAxisMap[0].scale);
    }
  }, [yAxisMap, onScaleUpdate]);
  return null;
};

const RSCChart = ({ data, onLineClick, onPinnedChange, pinnedSymbols = [], isResizable = false }) => {
  const [activeSeries, setActiveSeries] = useState(null);
  const lockedSeries = useMemo(() => new Set(pinnedSymbols), [pinnedSymbols]);
  const [hoverDate, setHoverDate] = useState(null);
  const [lockedDate, setLockedDate] = useState(null);
  const [legendSearch, setLegendSearch] = useState('');
  const [showAllLines, setShowAllLines] = useState(false);
  const [viewMode, setViewMode] = useState('RS_VALUES');

  // Drawing Tools State
  const [drawingMode, setDrawingMode] = useState('cursor'); // 'cursor', 'hline', 'vline', 'eraser'
  const [vLines, setVLines] = useState([]); // array of dates
  const [hLines, setHLines] = useState([]); // array of values
  const yScaleRef = useRef(null);

  const toggleLock = (name) => {
    const next = new Set(lockedSeries);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    // Notify parent of pinned change
    if (onPinnedChange) {
      onPinnedChange(Array.from(next));
    }
  };

  const transformed = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Transform data: Align all series to common dates
    const allDatesSet = new Set();
    data.forEach(series => {
      if (series.x) series.x.forEach(date => allDatesSet.add(date));
    });
    const sortedDates = Array.from(allDatesSet).sort();

    const seriesDataMap = data.map(series => {
      const map = {};
      if (series.x && series.y) {
        series.x.forEach((date, i) => map[date] = series.y[i]);
      }
      return { name: series.name, map };
    });

    const baseValues = {};
    seriesDataMap.forEach(s => {
      const firstPoints = [];
      for (const date of sortedDates) {
        const val = s.map[date];
        if (val !== undefined && val !== null && val > 0 && isFinite(val)) {
          firstPoints.push(val);
          if (firstPoints.length >= 5) break;
        }
      }

      if (firstPoints.length > 0) {
        const sorted = [...firstPoints].sort((a, b) => a - b);
        const median = sorted[Math.floor(sorted.length / 2)];
        const firstVal = firstPoints[0];

        // If the first value is reasonably close to the median of the first 5 (not a glitch)
        // use it as the base so the chart starts exactly at 1.0
        if (median > 0 && firstVal / median > 0.5 && firstVal / median < 2.0) {
          baseValues[s.name] = firstVal;
        } else {
          // Otherwise use the median to protect against the glitch
          baseValues[s.name] = median;
        }
      }
    });

    const lastValues = {};
    const lastDateIndex = {};

    return sortedDates.map((date, dateIdx) => {
      const entry = { date };
      seriesDataMap.forEach(s => {
        const rawVal = s.map[date];
        const base = baseValues[s.name];

        // Filter out zero/invalid values from the chart entirely
        if (rawVal !== undefined && rawVal !== null && rawVal > 0 && isFinite(rawVal) && base) {
          let normalizedVal = rawVal / base;

          // Sanity cap: RSC vs an index shouldn't realistically jump 10x in this context
          // High values are almost always data gaps/glitches in the benchmark divisor
          if (normalizedVal > 10) normalizedVal = null;

          entry[s.name] = normalizedVal;
          lastValues[s.name] = normalizedVal;
          lastDateIndex[s.name] = dateIdx;
        } else if (lastValues[s.name] !== undefined && lastDateIndex[s.name] !== undefined) {
          const gapSize = dateIdx - lastDateIndex[s.name];
          if (gapSize < 5) {
            entry[s.name] = lastValues[s.name];
          } else {
            entry[s.name] = null;
          }
        } else {
          entry[s.name] = null;
        }
      });
      return entry;
    });
  }, [data]);

  const colorMap = useMemo(() => {
    const map = {};
    if (data) {
      data.forEach((series, idx) => {
        map[series.name] = PALETTE[idx % PALETTE.length];
      });
    }
    return map;
  }, [data]);

  const visibleTopSeries = useMemo(() => {
    if (!transformed || transformed.length === 0) return new Set();
    
    let targetEntry = transformed[transformed.length - 1];
    let dateIndex = transformed.length - 1;

    if (lockedDate) {
      const idx = transformed.findIndex(e => String(e.date) === String(lockedDate));
      if (idx !== -1) {
        targetEntry = transformed[idx];
        dateIndex = idx;
      }
    }

    if (viewMode === 'RS_VALUES') {
      const top10 = Object.entries(targetEntry)
        .filter(([key]) => key !== 'date' && targetEntry[key] !== null)
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
        .slice(0, 10)
        .map(([name]) => name);
      return new Set(top10);
    } else {
      const pastIndex = Math.max(0, dateIndex - 1);
      const pastEntry = transformed[pastIndex];

      const getRanks = (entry) => {
        if (!entry) return {};
        const sorted = Object.entries(entry)
          .filter(([key, val]) => key !== 'date' && val !== null)
          .sort((a, b) => b[1] - a[1]);
        
        const ranks = {};
        sorted.forEach(([name, _], index) => {
          ranks[name] = index + 1;
        });
        return ranks;
      };

      const pastRanks = getRanks(pastEntry);
      const currentRanks = getRanks(targetEntry);

      const top10 = Object.entries(currentRanks)
        .map(([name, todayRank]) => {
          const yesterdayRank = pastRanks[name];
          let jump = null;
          if (yesterdayRank !== undefined) {
            jump = yesterdayRank - todayRank;
          }
          return { name, value: jump };
        })
        .filter(item => item.value !== null)
        .sort((a, b) => b.value - a.value)
        .slice(0, 10)
        .map(item => item.name);
      return new Set(top10);
    }
  }, [transformed, viewMode, lockedDate]);

  if (!data || data.length === 0) return <div className="flex items-center justify-center h-full text-zinc-500">No data available for selected period.</div>;

  return (
    <div className="flex h-full w-full bg-black/20 rounded-2xl border border-zinc-800/50 overflow-hidden">
      {/* LEFT: DATA INSPECTION COLUMN (Moved from right, replacing Active Series legend) */}
      <DataPanel
        data={transformed}
        activeSeries={activeSeries}
        lockedSeries={lockedSeries}
        date={lockedDate || hoverDate || transformed[transformed.length - 1]?.date}
        isLocked={!!lockedDate}
        onClearLock={() => setLockedDate(null)}
        setActiveSeries={setActiveSeries}
        toggleLock={toggleLock}
        colorMap={colorMap}
        showAllLines={showAllLines}
        setShowAllLines={setShowAllLines}
        isResizable={isResizable}
        viewMode={viewMode}
        setViewMode={setViewMode}
      />

      {/* CENTER: CHART COLUMN */}
      <div className="flex-1 min-w-0 relative flex flex-col p-2">
        <div className="flex-1 flex gap-2">
          
          {/* DRAWING TOOLBAR */}
          <div className="w-10 shrink-0 flex flex-col items-center gap-2 py-2 bg-zinc-900/50 border border-zinc-800 rounded-xl shadow-inner z-10">
            {[
              { id: 'cursor', icon: MousePointer2, title: 'Cursor / Lock Point' },
              { id: 'hline', icon: Minus, title: 'Horizontal Line (Snap to hovered value)' },
              { id: 'vline', icon: Minus, title: 'Vertical Line', className: 'rotate-90' },
              { id: 'eraser', icon: Eraser, title: 'Eraser (Click near line to remove)' },
            ].map(tool => (
              <button
                key={tool.id}
                onClick={() => setDrawingMode(tool.id)}
                title={tool.title}
                className={`p-1.5 rounded-lg transition-all ${
                  drawingMode === tool.id 
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 shadow-md' 
                    : 'text-zinc-500 hover:text-white hover:bg-zinc-800 border border-transparent'
                }`}
              >
                <tool.icon className={`w-4 h-4 ${tool.className || ''}`} />
              </button>
            ))}
            
            <div className="w-6 h-px bg-zinc-800/80 my-1" />
            
            <button
              onClick={() => { setHLines([]); setVLines([]); }}
              title="Clear All Drawings"
              className="p-1.5 rounded-lg text-zinc-600 hover:text-rose-400 hover:bg-rose-500/10 transition-all border border-transparent"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={transformed}
                style={{ cursor: drawingMode !== 'cursor' ? (drawingMode === 'eraser' ? 'cell' : 'crosshair') : 'default' }}
                margin={{ top: 20, right: 30, left: 10, bottom: 0 }}
              onMouseMove={(e) => {
                if (e && e.activeLabel) {
                  setHoverDate(e.activeLabel);
                } else {
                  setHoverDate(null);
                }
              }}
              onMouseLeave={() => setHoverDate(null)}
              onClick={(e) => {
                if (!e) return;
                if (drawingMode === 'cursor') {
                  if (e.activeLabel) {
                    setLockedDate(prev => prev === e.activeLabel ? null : e.activeLabel);
                  }
                } else if (drawingMode === 'vline') {
                  if (e.activeLabel) {
                    setVLines(prev => prev.includes(e.activeLabel) ? prev : [...prev, e.activeLabel]);
                  }
                } else if (drawingMode === 'hline') {
                  if (e.chartY !== undefined && yScaleRef.current) {
                    const yValue = yScaleRef.current.invert(e.chartY);
                    setHLines(prev => [...prev, yValue]);
                  }
                } else if (drawingMode === 'eraser') {
                  if (e.activeLabel && vLines.includes(e.activeLabel)) {
                    setVLines(prev => prev.filter(v => v !== e.activeLabel));
                    return; // Deleted vline, stop
                  }
                  if (e.chartY !== undefined && yScaleRef.current && hLines.length > 0) {
                    const clickYValue = yScaleRef.current.invert(e.chartY);
                    let closest = hLines[0];
                    let minDiff = Math.abs(hLines[0] - clickYValue);
                    for (let h of hLines) {
                      const diff = Math.abs(h - clickYValue);
                      if (diff < minDiff) {
                        closest = h;
                        minDiff = diff;
                      }
                    }
                    
                    // Delete if clicked within 5% difference of the value
                    if (minDiff < Math.abs(clickYValue * 0.05)) {
                      setHLines(prev => prev.filter(v => v !== closest));
                    }
                  }
                }
              }}
            >
              <Customized component={(props) => <ChartStateCatcher {...props} onScaleUpdate={(scale) => { yScaleRef.current = scale; }} />} />
              <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#666"
                fontSize={10}
                tickFormatter={(tick) => moment(tick).format('DD MMM YY')}
                interval="preserveStartEnd"
                minTickGap={50}
                tick={{ fill: '#fff' }}
              />
              <YAxis
                stroke="#666"
                fontSize={10}
                domain={['auto', 'auto']}
                orientation="left"
                tickFormatter={(val) => val.toFixed(2)}
                tick={{ fill: '#fff' }}
              />
              <Tooltip
                isAnimationActive={false}
                content={<CustomTooltip />}
                cursor={{ stroke: '#555', strokeWidth: 1, strokeDasharray: '3 3' }}
              />

              {lockedDate && (
                <ReferenceLine
                  x={lockedDate}
                  stroke="#10b981"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  label={{
                    position: 'top',
                    value: 'PINNED',
                    fill: '#10b981',
                    fontSize: 10,
                    fontWeight: 900,
                    letterSpacing: 2
                  }}
                />
              )}

              {/* DRAWN VERTICAL LINES */}
              {vLines.map((val, i) => (
                <ReferenceLine
                  key={`vline-${i}`}
                  x={val}
                  stroke="#3b82f6"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  label={{
                    position: 'insideTopLeft',
                    value: 'V-LINE',
                    fill: '#3b82f6',
                    fontSize: 10,
                    fontWeight: 800
                  }}
                />
              ))}

              {/* DRAWN HORIZONTAL LINES */}
              {hLines.map((val, i) => (
                <ReferenceLine
                  key={`hline-${i}`}
                  y={val}
                  stroke="#eab308"
                  strokeWidth={1.5}
                  strokeDasharray="4 4"
                  label={{
                    position: 'insideBottomLeft',
                    value: val.toFixed(2),
                    fill: '#eab308',
                    fontSize: 11,
                    fontWeight: 800
                  }}
                />
              ))}

              {data.map((series, idx) => {
                const isTop10 = visibleTopSeries.has(series.name);
                const isLocked = lockedSeries.has(series.name);
                const isActive = activeSeries === series.name;
                
                if (!showAllLines && !isTop10 && !isLocked && !isActive) {
                  return null;
                }

                const isHighlighted = isLocked || isActive;
                const anyActive = activeSeries !== null || lockedSeries.size > 0;
                const isDimmed = anyActive && !isHighlighted;

                return (
                  <Line
                    key={series.name}
                    type="monotone"
                    dataKey={series.name}
                    stroke={PALETTE[idx % PALETTE.length]}
                    dot={false}
                    strokeWidth={isHighlighted ? 3 : 1.5}
                    strokeOpacity={isDimmed ? 0.4 : 1.0}
                    activeDot={{ r: 4, strokeWidth: 0, fill: PALETTE[idx % PALETTE.length] }}
                    isAnimationActive={false}
                    onMouseEnter={() => setActiveSeries(series.name)}
                    onMouseLeave={() => setActiveSeries(null)}
                    onClick={() => onLineClick && onLineClick(series.name)}
                    style={{ cursor: 'pointer' }}
                    connectNulls={false}
                  />
                );
              })}
              <Brush
                dataKey="date"
                height={24}
                stroke="#333"
                fill="#0a0a0a"
                tickFormatter={(tick) => moment(tick).format('DD MMM YY')}
                className="text-[11px]"
                gap={5}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        </div>
      </div>
    </div>
  );
};

export default RSCChart;
